import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppNotification, NotificationsState } from "./models/notificationModels";
import notificationsApi from "./api/notificationsApi";

export const fetchNotifications = createAsyncThunk(
  "notificationsSlice/fetchNotifications",
  async () => {
    return notificationsApi.getNotifications(1, 50);
  }
);

export const fetchUnreadCount = createAsyncThunk(
  "notificationsSlice/fetchUnreadCount",
  async () => {
    return notificationsApi.getUnreadCount();
  }
);

export const markAsRead = createAsyncThunk(
  "notificationsSlice/markAsRead",
  async (notificationId: string) => {
    await notificationsApi.markAsRead(notificationId);
    return notificationId;
  }
);

export const markAllAsRead = createAsyncThunk(
  "notificationsSlice/markAllAsRead",
  async () => {
    await notificationsApi.markAllAsRead();
  }
);

const initialState: NotificationsState = {
  notifications: [],
  unreadCount: 0,
  loading: false,
  error: undefined,
  isPanelOpen: false,
};

const notificationsSlice = createSlice({
  name: "notificationsSlice",
  initialState,
  reducers: {
    togglePanel: (state) => {
      state.isPanelOpen = !state.isPanelOpen;
    },
    closePanel: (state) => {
      state.isPanelOpen = false;
    },
    addNotification: (state, action: PayloadAction<AppNotification>) => {
      state.notifications.unshift(action.payload);
      state.unreadCount += 1;
    },
    setUnreadCount: (state, action: PayloadAction<number>) => {
      state.unreadCount = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchNotifications
      .addCase(fetchNotifications.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.loading = false;
        state.notifications = action.payload;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // fetchUnreadCount
      .addCase(fetchUnreadCount.fulfilled, (state, action) => {
        state.unreadCount = action.payload;
      })
      // markAsRead
      .addCase(markAsRead.fulfilled, (state, action) => {
        const notification = state.notifications.find(
          (n) => n.id === action.payload
        );
        if (notification) {
          notification.isRead = true;
        }
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      })
      // markAllAsRead
      .addCase(markAllAsRead.fulfilled, (state) => {
        state.notifications.forEach((n) => {
          n.isRead = true;
        });
        state.unreadCount = 0;
      });
  },
});

export const { togglePanel, closePanel, addNotification, setUnreadCount } =
  notificationsSlice.actions;

export default notificationsSlice.reducer;
